/**
 * @file CatalogListGroups.tsx
 * @description Содержимое групп вкладки «Список» панели «Каталог»
 * (phase19.16 Р2а; uiux-design-r2a §2; db-design-r2a, ответ 1; решения
 * 2026-10-12): сетка по устройствам и товаров на странице; шапка
 * категории; плитки подкатегорий (фото категории квадратом); фильтры
 * (справа на ПК — не настраивается); сортировки. Общий контракт групп —
 * `options` / `onChange` (engineering-principles «Композиция»).
 *
 * Группа «Шапка категории» — отдельный файл `CategoryHeaderGroup` (схемы
 * вида шапки и поля баннера, решения 2026-10-12).
 *
 * SB122 (uiux-settings-groups §3): внутри группы — подпись поля `Label`
 * вместе с полем (`GroupField`), подсказки — `FieldHint`; ссылка в другой раздел
 * кабинета — со знаком «→».
 */

import { CATALOG_SORT_KEYS, CATALOG_SORT_LABELS, type CatalogSort, type SiteCatalogOptions } from "@chaiberi/shared";
import { Link } from "react-router";

import { Label } from "../../ui/label";
import { FieldHint, GroupField } from "../../ui/settings-groups";
import { SelectField } from "../../ui/SelectField";
import { CheckRow } from "../CheckRow";
import { Segmented } from "../Segmented";
import { useCurrentContext } from "../../../auth/hooks";
import { gridHint, maxDesktopColumns, setDefaultSort, toggleSort } from "../../../lib/site-view/catalog-form";

export interface CatalogGroupProps {
  options: SiteCatalogOptions;
  onChange: (options: SiteCatalogOptions) => void;
}

type Grid = SiteCatalogOptions["grid"];
const nums = <T extends number>(values: readonly T[], disabledAbove = Infinity) => values.map((n) => ({ value: String(n), label: String(n), disabled: n > disabledAbove }));

export function GridGroup({ options, onChange }: CatalogGroupProps) {
  const grid = options.grid;
  const setGrid = (patch: Partial<Grid>) => onChange({ ...options, grid: { ...grid, ...patch } });
  const max = maxDesktopColumns(options.card.template);
  const hint = gridHint(options);
  return (
    <>
      <GroupField label="В ряд на ПК">
        <Segmented aria-label="Товаров в ряд на ПК" options={nums([2, 3, 4] as const, max)} value={String(grid.desktop)} onChange={(v) => setGrid({ desktop: Number(v) as Grid["desktop"] })} />
        {max < 4 ? <FieldHint className="mt-0">Карточка «Фото слева» — не больше 2 в ряд на ПК.</FieldHint> : null}
        {hint ? <FieldHint className="mt-0 text-warning">{hint}</FieldHint> : null}
      </GroupField>
      <GroupField label="На планшете">
        <Segmented aria-label="Товаров в ряд на планшете" options={nums([2, 3] as const)} value={String(grid.tablet)} onChange={(v) => setGrid({ tablet: Number(v) as Grid["tablet"] })} />
      </GroupField>
      <GroupField label="На телефоне">
        <Segmented aria-label="Товаров в ряд на телефоне" options={nums([1, 2] as const)} value={String(grid.phone)} onChange={(v) => setGrid({ phone: Number(v) as Grid["phone"] })} />
      </GroupField>
      <GroupField label="Товаров на странице">
        <Segmented aria-label="Товаров на странице" options={nums([12, 24, 36, 48] as const)} value={String(options.perPage)} onChange={(v) => onChange({ ...options, perPage: Number(v) as SiteCatalogOptions["perPage"] })} />
      </GroupField>
    </>
  );
}

const TILE_LOOK = [
  { value: "below" as const, label: "Подпись под фото" },
  { value: "overlay" as const, label: "Подпись поверх фото" },
];
const TILE_PHONE = [
  { value: "scroll" as const, label: "Лентой" },
  { value: "grid2" as const, label: "По две" },
];

export function SubcategoriesGroup({ options, onChange }: CatalogGroupProps) {
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const tiles = options.subcategoryTiles;
  const set = (patch: Partial<typeof tiles>) => onChange({ ...options, subcategoryTiles: { ...tiles, ...patch } });
  return (
    <>
      <CheckRow checked={tiles.on} onChange={(on) => set({ on })}>
        Подкатегории плитками над списком
      </CheckRow>
      {tiles.on ? (
        <>
          <GroupField label="Подпись">
            <Segmented aria-label="Подпись плитки" options={TILE_LOOK} value={tiles.look} onChange={(look) => set({ look })} />
          </GroupField>
          <GroupField label="На телефоне">
            <Segmented aria-label="Плитки на телефоне" options={TILE_PHONE} value={tiles.phone} onChange={(phone) => set({ phone })} />
          </GroupField>
        </>
      ) : (
        // Без плиток настройки подписи не действуют — пояснение на месте (SB122).
        <FieldHint className="mt-0">Плитки выключены — над списком товаров подкатегорий нет, подпись и вид на телефоне не нужны.</FieldHint>
      )}
      <FieldHint className="mt-0">
        Фото берётся из категории, квадратом.{" "}
        <Link to={`/shop/${slug}/catalog/categories`} className="inline-flex min-h-12 items-center font-medium text-primary hover:underline">
          Изменить категории →
        </Link>
      </FieldHint>
    </>
  );
}

export function FiltersGroup({ options, onChange }: CatalogGroupProps) {
  const filters = options.filters;
  return (
    <>
      <FieldHint className="mt-0">На ПК фильтры справа от списка, на телефоне — кнопкой «Фильтры».</FieldHint>
      <div className="flex min-w-0 flex-col">
        <CheckRow checked={filters.categoryTree} onChange={(categoryTree) => onChange({ ...options, filters: { ...filters, categoryTree } })}>
          Категории деревом
        </CheckRow>
        <CheckRow checked={filters.tags} onChange={(tags) => onChange({ ...options, filters: { ...filters, tags } })}>
          Метки
        </CheckRow>
      </div>
    </>
  );
}

export function SortGroup({ options, onChange }: CatalogGroupProps) {
  const { enabled } = options.sorts;
  return (
    <>
      <GroupField label="По умолчанию">
        <SelectField
          ariaLabel="Сортировка по умолчанию"
          value={options.sorts.default}
          options={enabled.map((key) => ({ value: key, label: CATALOG_SORT_LABELS[key] }))}
          onChange={(v) => onChange(setDefaultSort(options, v as CatalogSort))}
        />
      </GroupField>
      <div className="flex min-w-0 flex-col">
        <Label>Покупатель может выбрать</Label>
        {CATALOG_SORT_KEYS.map((key) => {
          const last = enabled.length === 1 && enabled[0] === key;
          return (
            <CheckRow key={key} checked={enabled.includes(key)} disabled={last} onChange={(on) => onChange(toggleSort(options, key, on))}>
              {CATALOG_SORT_LABELS[key]}
            </CheckRow>
          );
        })}
      </div>
    </>
  );
}
